import type { Cart } from '@iheartjane/data-access';
import { useMemo } from 'react';

import { selectCart } from '../../../customer/redux/cart';
import { selectPosCart } from '../../../customer/redux/posCart';
import { useCustomerSelector } from '../../../customer/selectors';
import { mapV1CartToV3 } from './mapV1CartToV3';
import type { V1CartInput } from './mapV1CartToV3';

/**
 * The read side of the cart facade: reads the legacy v1 Redux cart slice and
 * returns the canonical v3 `Cart` shape via `mapV1CartToV3`, plus normalized
 * load/mutation flags and derived reads. No flag, no behavior change — this is
 * v1 Redux wearing a v3 face so consumers can migrate onto the facade before the
 * v3 backing exists.
 * TODO(carts-v3): remove once the v3 backing is the only source.
 */
export interface CartFacadeState {
  /** v3-shaped cart, or `null` when the slice holds no cart. */
  cart: Cart | null;
  /** Total item count (sum of product counts), not the distinct-line count. */
  cartSize: number;
  /**
   * True once the cart has been fetched at least once (the slice's `hasLoaded`).
   * Unlike `isLoading`/`isFetching` this is not React-Query-derived — it mirrors
   * the raw slice flag so a consumer's initial-load gate stays byte-identical.
   */
  hasLoaded: boolean;
  /** Whether any special/discount is applied to the cart. */
  hasSpecial: boolean;
  /**
   * True while a loyalty redemption started by `useCartActions().applyLoyalty`
   * is in flight. Deliberately narrower than `isMutating` (which folds in
   * checkout and four other flags): this backs the double-apply guard on the
   * reward buttons, so widening it would block a legitimate apply during an
   * unrelated cart mutation. Exposed on the facade — rather than left as a
   * direct `selectCart` read at each call site — so the guard and the write it
   * guards move together when `applyLoyalty` is repointed at the v3 mutation.
   */
  isApplyingLoyalty: boolean;
  /** React-Query-style: any cart fetch in flight. */
  isFetching: boolean;
  /** React-Query-style: an initial load with no cart yet. */
  isLoading: boolean;
  /**
   * True while a *trackable* cart mutation is in flight — add-to-cart, checkout,
   * loyalty-apply, reservation-mode change, or unavailable-product deletion.
   * Composed here because the v1 slice has no single mutation flag, so pending UI
   * can be added in one place rather than retrofitted per call site. This does
   * NOT cover every facade action: `updateProduct`, `removeProduct`,
   * `updateDeliveryAddress`, and `removeLoyalty` dispatch thunks that set no
   * in-flight flag on the v1 slice, so they are invisible here (see the
   * composition comment for the full list).
   */
  isMutating: boolean;
  /** Canonical order total consumers display. */
  orderTotal: number;
}

export const useCartState = (): CartFacadeState => {
  const {
    cart: v1Cart,
    isLoading,
    hasLoaded,
    isAddingItemToCart,
    isApplyingCrmRedemption,
    isChangingReservationModes,
    isCheckingOut,
    isDeletingUnavailableProducts,
    isFixingUnavailableCheckout,
  } = useCustomerSelector(selectCart);

  // POS totals live on a separate slice, fetched from a separate endpoint — not
  // on the cart object. Read them here (the mapper stays Redux-free) so consumers
  // that branch on `pos_cart.totals` (e.g. `getCartLineItems`) see the same value
  // and take the same tax/total branch as the pre-facade `selectPosCart` path.
  const posCartTotals =
    useCustomerSelector(selectPosCart).posCart.totals ?? null;

  // An absent cart has an empty uuid (the slice's default). Map only real carts.
  // The cast crosses two axes: (1) the slice stores the cart `DeepReadonly` and the
  // pure mapper reads a mutable cart it never mutates; (2) the slice's runtime cart
  // is the `@jane/shared/models` shape, which `V1CartInput` models directly (widened
  // id fields + `amounts_manager`). Both are safe: the read is non-mutating and
  // `V1CartInput` reflects the real runtime fields rather than papering over them.
  const cart = useMemo(
    () =>
      v1Cart.uuid
        ? mapV1CartToV3(v1Cart as unknown as V1CartInput, posCartTotals)
        : null,
    [v1Cart, posCartTotals]
  );

  const cartSize = cart
    ? cart.products.reduce((total, product) => total + product.count, 0)
    : 0;

  const hasSpecial = Boolean(
    cart &&
    (cart.cart_discount_special_id != null ||
      cart.group_special_id != null ||
      cart.brand_discounts.length > 0 ||
      cart.products.some((product) => product.special_id != null))
  );

  return {
    cart,
    hasLoaded,
    isLoading: isLoading && !hasLoaded,
    isFetching: isLoading,
    // Every trackable in-flight flag that backs a facade action. Several facade
    // mutations set NO in-flight flag on the v1 slice and so cannot be reflected
    // here: `updateProduct` (`updateCart`) and `removeProduct` (`deleteCartItem`)
    // dispatch only their success actions; `removeLoyalty` (`removeCrmRedemption`)
    // toggles nothing; and `updateDeliveryAddress` (`initiateUpdateDeliveryAddress`)
    // sets neither a flag nor `isLoading`, so it is invisible to `isFetching` too.
    // These are genuinely untrackable on the Redux path — do not invent flags.
    isMutating:
      isAddingItemToCart ||
      isApplyingCrmRedemption ||
      isChangingReservationModes ||
      isCheckingOut ||
      isDeletingUnavailableProducts ||
      isFixingUnavailableCheckout,
    isApplyingLoyalty: isApplyingCrmRedemption,
    cartSize,
    hasSpecial,
    orderTotal: cart?.order_total ?? 0,
  };
};
